import { useEffect, useState } from 'react';
import { AuthProvider } from './auth.d';
import { useGetUserQuery } from './auth.api.slice';

const STORAGE_KEY = 'lastAuthProvider';

function readStoredProvider(): AuthProvider | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === AuthProvider.Email || stored === AuthProvider.Google) {
      return stored;
    }
  } catch {
    // Storage disabled; the badge is a hint, not worth failing a render over.
  }
  return null;
}

/**
 * Records the provider once the user is authenticated, never on the click:
 * the Google button leaves the site, so a click is only an attempt.
 *
 * Belongs at the app root. Inside the login dialog it would never see the
 * Google case, because that returns by full redirect with the dialog closed.
 */
export function useRecordAuthProvider(): void {
  const { data: user } = useGetUserQuery();
  const provider = user?.authProvider;

  useEffect(() => {
    if (!provider) {
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, provider);
    } catch {
      // As above.
    }
  }, [provider]);
}

// Sits outside the button: inside, it would append to the accessible name and
// break every getByRole('button', { name: 'Continue', exact: true }).
export function LastUsedBadge({ provider }: { provider: AuthProvider }) {
  const [lastUsed] = useState(readStoredProvider);

  if (lastUsed !== provider) {
    return null;
  }

  return (
    <span className="pointer-events-none absolute -top-2 right-3 rounded-full border bg-background px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      Last used
    </span>
  );
}
